"use client"

import { Endpoints } from "@/remote/endpoints"
import { Button } from "@/view/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/view/components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/view/components/ui/table"
import { useToast } from "@/view/hooks"
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Eye, FileText, ImageIcon, Lock, MoreHorizontal, Trash2, Unlock, PenIcon as UserPen } from "lucide-react"
import * as React from "react"
import { useSearchParams } from "react-router-dom"
import { useRemoteClientPut } from "../../configs/http-client"
import { CatchMessageError } from "../../remote/catch-message-error"
import { convertParamsToJson, useCashfitParams } from "../hooks/use-params"
import { DialogDelete } from "../modals/modal-delete"
import { convertDateDisplayFormat } from "../utils/formatDateDisplay"
import { Badge } from "./ui/badge"
import { ScrollArea } from "./ui/scroll-area"

interface Props {
  data: any[]
  getRowsSelecteds?: (data: any) => void
  getRefreshList: () => void
}

export function DataTable({ data, getRefreshList }: Props) {
  const [params] = useSearchParams()
  const { setParams } = useCashfitParams(convertParamsToJson(params))
  const { httpClientPut } = useRemoteClientPut()
  const { showToast } = useToast()

  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})
  const [loadingBlock, setLoadingBlock] = React.useState<number | null>(null)
  const [dialogDelete, setDialogDelete] = React.useState(false)
  const [dataId, setDataId] = React.useState(0)

  const handleBlockUnblock = async (studentId: number, currentStatus: number, data: any) => {
    if (loadingBlock === studentId) return

    setLoadingBlock(studentId)

    try {
      const newStatus = currentStatus === 3 ? 1 : 3 // Toggle between blocked (3) and active (1)

      await httpClientPut("/alunos", {
        ...data,
        status: newStatus,
      })

      const action = newStatus === 3 ? "bloqueado" : "desbloqueado"
      showToast(`Aluno ${action} com sucesso!`, "success")
      getRefreshList() // Refresh the list to show updated status
    } catch (error) {
      console.error("Erro ao alterar status do aluno:", error)
      CatchMessageError(error)
      showToast("Erro ao alterar status do aluno", "error")
    } finally {
      setLoadingBlock(null)
    }
  }

  const openDialogDelete = (id: number) => {
    setDataId(id)
    setDialogDelete(true)
  }

  const closeDialogDelete = () => {
    setDialogDelete(false)
  }

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      header: "",
      cell: () => <ImageIcon />,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "nome",
      header: "Nome",
      cell: ({ row }) => {
        return (
          <div
            className="capitalize cursor-pointer text-acelerafit-gray-dark hover:text-cashfit-green-200"
            onClick={() =>
              setParams({
                tabContentId: row?.original?.id || 0,
                tabSelected: "overview" as any,
              })
            }
          >
            {row.getValue("nome")}
          </div>
        )
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <div className="lowercase">{row.getValue("email")}</div>,
    },
    {
      accessorKey: "cpf",
      header: "CPF",
      cell: ({ row }) => {
        const formatCpf = (cpf: string) => {
          return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
        }

        const formattedCpf = formatCpf(row.getValue("cpf"))

        return <div className="lowercase">{formattedCpf}</div>
      },
    },
    {
      accessorKey: "dataNascimento",
      header: "Data de Nascimento",
      cell: ({ row }) => {
        const formatBirthDate = (birthDate?: string) => {
          if (!birthDate) return ""
          return convertDateDisplayFormat(birthDate.split("T")[0]) ?? ""
        }

        const formattedBirthDate = formatBirthDate(row.getValue("dataNascimento"))

        return <div className="lowercase">{formattedBirthDate}</div>
      },
    },
    {
      accessorKey: "status",
      header: "situação",
      cell: ({ row }) => {
        const status = {
          "0": "deletado",
          "1": "ativo",
          "2": "pendente",
          "3": "bloqueado",
          "4": "inadimplente",
          "5": "cancelado",
        }

        const statusColor = {
          "0": "bg-red-200 border-red-300",
          "1": "bg-green-200 border-green-300",
          "2": "bg-yellow-200 border-yellow-300",
          "3": "bg-gray-200 border-gray-300",
          "4": "bg-orange-200 border-orange-300",
          "5": "bg-purple-200 border-purple-300",
        }

        return (
          <div className="lowercase">
            <Badge
              variant={"outline"}
              className={statusColor[(row.getValue("status") || 0).toString() as keyof typeof statusColor]}
            >
              {status[row.getValue("status")?.toString() as keyof typeof status]}
            </Badge>
          </div>
        )
      },
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const studant = row.original
        const isBlocked = studant.status === 3
        const isLoading = loadingBlock === studant.id

        return (
          <DropdownMenu>
            {dialogDelete && (
              <DialogDelete
                updateList={getRefreshList}
                closeDialog={closeDialogDelete}
                id={dataId}
                isOpen={dialogDelete}
                url={Endpoints.students}
                label="Aluno"
              />
            )}
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuItem
                className="flex gap-2 cursor-pointer"
                onClick={() =>
                  setParams({
                    tabContentId: studant.id,
                    tabSelected: "overview" as any,
                  })
                }
              >
                <Eye height={24} width={30} className="text-blue-500 cursor-pointer p-1 hover:text-blue-400" />
                Visão Geral
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex gap-2 cursor-pointer"
                onClick={() =>
                  setParams({
                    tabContentId: studant.id,
                    tabSelected: "register-member" as any,
                  })
                }
              >
                <UserPen height={24} width={30} className="text-blue-500 cursor-pointer p-1 hover:text-blue-400" />
                Cadastro
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex gap-2 cursor-pointer"
                onClick={() =>
                  setParams({
                    tabContentId: studant.id,
                    tabSelected: "contract" as any,
                  })
                }
              >
                <FileText height={24} width={30} className="text-blue-500 cursor-pointer p-1 hover:text-blue-400" />
                Contrato
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex gap-2 cursor-pointer"
                onClick={() => handleBlockUnblock(studant.id, studant.status, studant)}
                disabled={isLoading}
              >
                {isBlocked ? (
                  <Unlock height={24} width={30} className="text-green-500 cursor-pointer p-1 hover:text-green-400" />
                ) : (
                  <Lock height={24} width={30} className="text-orange-500 cursor-pointer p-1 hover:text-orange-400" />
                )}
                {isLoading ? "Processando..." : isBlocked ? "Desbloquear" : "Bloquear"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="flex gap-2 cursor-pointer" onClick={() => openDialogDelete(studant.id || 0)}>
                <Trash2 height={24} width={30} className="text-red-500 cursor-pointer p-1 hover:text-red-400" />
                Apagar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 100 } },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  return (
    <div className="w-full">
      <div className="rounded-md border min-w-full">
        <ScrollArea className="m-4 h-container-members-list relative">
          <Table className="border-none">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center"></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </div>
  )
}
